import React from 'react'
import Text, { TextScale } from 'particles/Text'
import styled from 'particles/themes/styled'
import RadioButton, { RadioButtonSize } from '.'
import Label, { LabelSize, LinkConfig } from 'particles/Label'
import { useField, useFormikContext } from 'formik'
import _ from 'lodash'
import CommentContainer from 'components/Shared/Comment'

type RadioButtonGroupOrientation = 'vertical' | 'horizontal'
type RadioButtonGroupSize = 'm' | 's'

export interface OptionState<T> {
    label: string
    value: T
}

interface RadioButtonGroupProps<T> {
    name: string
    orientation?: RadioButtonGroupOrientation
    size?: RadioButtonGroupSize
    isRequired?: boolean
    title?: string
    subtitle?: string
    isDisabled?: boolean
    isReadOnly?: boolean
    options: OptionState<T>[]
    selectedOption?: T
    onClick?: (value: T) => void
    errorMessage?: string
    labelSize?: LabelSize
    link?: LinkConfig
    tooltip?: string
    dataTestId?: string
    isCommentable?: boolean
    isCommented?: boolean
    onLabelClick?: () => void
    identifier?: string
}

interface RadioButtonGroupContainerProps {
    size: RadioButtonGroupSize
}

const RadioButtonGroupContainer = styled.div<RadioButtonGroupContainerProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[200]};
    .radioButtonGroup-label {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: ${({ theme }) => theme.spacing[100]};
        align-self: stretch;
    }
    .radioButtonGroup-title {
        display: flex;
        align-items: flex-start;
        gap: ${({ theme }) => theme.spacing[100]};
        align-self: stretch;
    }
    .radioButtons {
        display: flex;
        align-items: flex-start;
        align-self: stretch;
    }
    .radioButtons-vertical {
        flex-direction: column;
        gap: ${({ theme, size }) =>
            ({
                m: theme.spacing[300],
                s: theme.spacing[200],
            })[size]};
    }
    .radioButtons-horizontal {
        align-content: flex-start;
        gap: ${({ theme, size }) =>
            ({
                m: `${theme.spacing[300]} ${theme.spacing[400]}`,
                s: `${theme.spacing[200]} ${theme.spacing[300]}`,
            })[size]};
        flex-wrap: wrap;
    }
    .radioButtons-section {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: ${({ theme }) => theme.spacing[100]};
        align-self: stretch;
    }
`

const sizeMap: Record<
    RadioButtonGroupSize,
    { titleTextScale: TextScale; subtitleTextScale: TextScale; radioButtonSize: RadioButtonSize }
> = {
    m: {
        titleTextScale: 'bodyM500',
        subtitleTextScale: 'bodyS400',
        radioButtonSize: 'm',
    },
    s: {
        titleTextScale: 'bodyS500',
        subtitleTextScale: 'caption400',
        radioButtonSize: 's',
    },
}

const RadioButtonGroup = <T extends string | number | boolean>({
    name,
    title,
    subtitle,
    size = 'm',
    orientation = 'vertical',
    isRequired,
    isDisabled,
    isReadOnly,
    options,
    selectedOption,
    onClick,
    errorMessage,
    labelSize,
    link,
    tooltip,
    dataTestId,
    isCommentable = false,
    isCommented = false,
    onLabelClick,
    identifier,
}: RadioButtonGroupProps<T>) => {
    const groupSize = sizeMap[size]
    let isSelectedEmpty = true
    return (
        <RadioButtonGroupContainer size={size} data-testid={dataTestId}>
            {(title || subtitle) && (
                <CommentContainer identifier={identifier}>
                    <Label
                        title={title || ''}
                        subtext={subtitle}
                        isRequired={isRequired}
                        className="radioButtonGroup-label"
                        size={labelSize ? labelSize : size}
                        link={link}
                        tooltip={tooltip}
                        isCommentable={isCommentable}
                        isCommented={isCommented}
                        onClick={onLabelClick}
                    />
                </CommentContainer>
            )}
            <div className="radioButtons-section">
                <div
                    className={`radioButtons ${
                        orientation === 'horizontal' ? 'radioButtons-horizontal' : 'radioButtons-vertical'
                    }`}
                >
                    {options.map((option, index) => {
                        const isOptionSelected = _.isEqual(selectedOption, option.value)
                        isSelectedEmpty = isOptionSelected ? false : isSelectedEmpty

                        // If the group is readonly and nothing is selected. Show an empty state
                        if (isReadOnly && isSelectedEmpty && index === options.length - 1) {
                            return (
                                <RadioButton
                                    name={name}
                                    key={`${name}-radioButtonGroup-${index}`}
                                    label={option.label}
                                    selected={isOptionSelected}
                                    onChange={() => onClick?.(option.value)}
                                    isDisabled={isDisabled}
                                    size={groupSize.radioButtonSize}
                                    isReadOnly={isReadOnly}
                                    dataTestId={dataTestId}
                                />
                            )
                        }

                        return (isReadOnly && isOptionSelected) || !isReadOnly ? (
                            <RadioButton
                                key={`radioButtonGroup-${index}`}
                                label={option.label}
                                selected={isOptionSelected}
                                onChange={() => onClick?.(option.value)}
                                isDisabled={isDisabled}
                                size={groupSize.radioButtonSize}
                                isReadOnly={isReadOnly}
                                dataTestId={dataTestId}
                            />
                        ) : null
                    })}
                </div>
                {!isReadOnly && !isDisabled && errorMessage && (
                    <Text scale="caption400" color="error">
                        {errorMessage}
                    </Text>
                )}
            </div>
        </RadioButtonGroupContainer>
    )
}
interface FormikRadioButtonGroupProps<T> extends Omit<RadioButtonGroupProps<T>, 'onClick'> {
    onClick?: (value: T) => void
    showError?: boolean // set false if want to use external error state to control the error display
    validateByTouch?: boolean // set to false if you want to use an external error state to control error display. It will show errors without requiring the field to be touched.
    defaultSelected?: T
}

const FormikRadioButtonGroup = <T extends string | number | boolean>(props: FormikRadioButtonGroupProps<T>) => {
    const { name, onClick, showError = true, validateByTouch = true, defaultSelected } = props
    const [field, meta] = useField<T>(name)
    const { setFieldValue, setFieldTouched } = useFormikContext()
    const hasError = showError && !!meta.error && (!validateByTouch || meta.touched)

    const fieldValue = field.value

    const handleChecked = (optionValue: T): void => {
        setFieldValue(field.name as never, optionValue)
        if (!meta.touched) setFieldTouched(field.name as never, true, false)
        onClick?.(optionValue)
    }

    return (
        <RadioButtonGroup
            {...props}
            onClick={handleChecked}
            errorMessage={(hasError && meta.error) || ''}
            selectedOption={fieldValue ?? defaultSelected}
        />
    )
}

export { FormikRadioButtonGroup }
export default RadioButtonGroup
